import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { getConfig, reverseGeocode } from '../services/routexApi';
import { Place, Location, TravelMode } from '../types';
import {
  formatPlaceTypeLabel,
  getPlaceQueryTagKey,
  orderPlacesForMap,
} from '../utils/rankedPlaceTypeLegend';

// Extend Window interface for Google Maps
declare global {
  interface Window {
    google: typeof google;
    initGoogleMaps: () => void;
  }
}

interface GoogleMapProps {
  center?: Location;
  zoom?: number;
  places?: Place[];
  polyline?: string; // Selected route polyline (yellow for suggested routes, black for fastest)
  fastestRoutePolyline?: string; // Fastest route polyline (black, always shown)
  currentLocation?: Location;
  startLocation?: Location;
  endLocation?: Location;
  onMapLoad?: (map: google.maps.Map) => void;
  onPlaceClick?: (place: Place) => void;
  onMapClick?: (location: Location, address?: string) => void;
  isSelectingLocation?: 'start' | 'end' | null;
  hasPreferences?: boolean; // Whether the route has preferences/stops
  travelMode?: TravelMode; // Affects route line style: solid (car), dashed (bike), dotted (walk), etc.
  className?: string;
}

// Default location (Sydney) - used as fallback
const DEFAULT_LOCATION = { lat: -33.8688, lng: 151.2093 };

function getRankedPlaceEmoji(typeKey: string): string {
  const key = (typeKey || '').toLowerCase();
  if (key.includes('coffee') || key.includes('cafe')) return '☕';
  // Keep grocery/supermarket checks before restaurant/food checks.
  if (
    key.includes('supermarket') ||
    key.includes('grocery') ||
    key.includes('grocer') ||
    key.includes('food_store') ||
    key.includes('convenience_store')
  ) {
    return '🛒';
  }
  if (key.includes('restaurant') || key.includes('food') || key.includes('dining')) return '🍽️';
  if (key.includes('bar') || key.includes('pub')) return '🍺';
  if (key.includes('post') || key.includes('postal') || key.includes('post_office')) return '📮';
  if (key.includes('landmark') || key.includes('historic') || key.includes('historical') || key.includes('monument')) return '🏰';
  if (key.includes('gas') || key.includes('fuel') || key.includes('petrol') || key.includes('service_station')) return '⛽';
  if (key.includes('pharmacy') || key.includes('drugstore') || key.includes('chemist')) return '💊';
  if (key.includes('museum') || key.includes('art') || key.includes('gallery')) return '🏛️';
  if (key.includes('park') || key.includes('garden') || key.includes('nature')) return '🌳';
  if (key.includes('shopping') || key.includes('mall') || key.includes('store') || key.includes('market')) return '🛍️';
  if (key.includes('hotel') || key.includes('stay') || key.includes('accommodation')) return '🏨';
  if (key.includes('gym') || key.includes('fitness') || key.includes('sport')) return '🏋️';
  if (key.includes('spa') || key.includes('wellness')) return '💆';
  if (key.includes('beach') || key.includes('sea')) return '🏖️';
  if (key.includes('library') || key.includes('book')) return '📚';
  if (key.includes('movie') || key.includes('cinema') || key.includes('theatre') || key.includes('theater')) return '🎬';
  if (key.includes('music') || key.includes('concert')) return '🎵';
  return '📍';
}

function makeEmojiMarkerIcon(
  emoji: string,
  googleMaps: typeof google.maps
): google.maps.Icon {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="15.5" fill="#ffffff" stroke="#2563eb" stroke-width="2" />
    <text x="17" y="22" text-anchor="middle" font-size="16">${emoji}</text>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new googleMaps.Size(34, 34),
    anchor: new googleMaps.Point(17, 17),
  };
}

/** Return polyline icons for dashed/dotted styles (Google Maps). Solid = no icons. */
function getPolylineIconsForTravelMode(
  travelMode: TravelMode | undefined,
  strokeColor: string
): google.maps.IconSequence[] | undefined {
  if (!travelMode || travelMode === 'DRIVING' || travelMode === 'TRANSIT') return undefined; // Solid line (car and public transport)

  switch (travelMode) {
    case 'WALKING':
      // Dotted: thicker dots, more visible
      return [
        {
          icon: {
            path: 'M 0,-0.5 0,0.5',
            strokeColor,
            strokeOpacity: 1,
            strokeWeight: 4,
            scale: 5,
          },
          offset: '0',
          repeat: '12px',
        },
      ];
    case 'BICYCLING':
      // Dashed: thicker dashes
      return [
        {
          icon: {
            path: 'M 0,-1 0,1',
            strokeColor,
            strokeOpacity: 1,
            strokeWeight: 4,
            scale: 5,
          },
          offset: '0',
          repeat: '20px',
        },
      ];
    default:
      return undefined;
  }
}

// Google Maps script loader - track state across component lifecycle
let scriptLoadPromise: Promise<void> | null = null;

// Helper to get user's current location
const getUserLocation = (): Promise<Location> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(DEFAULT_LOCATION);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        console.warn('Geolocation error:', error.message);
        resolve(DEFAULT_LOCATION);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 60000, // Cache for 1 minute
      }
    );
  });
};

const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  // If Google Maps is already loaded, resolve immediately
  if (window.google?.maps) {
    return Promise.resolve();
  }

  // If we're already loading, return the existing promise
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  // Create new loading promise
  scriptLoadPromise = new Promise((resolve, reject) => {
    // Check again in case it loaded between checks
    if (window.google?.maps) {
      resolve();
      return;
    }

    window.initGoogleMaps = () => {
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      scriptLoadPromise = null; // Reset so it can be retried
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

export const GoogleMap: React.FC<GoogleMapProps> = ({
  center,
  zoom = 14,
  places = [],
  polyline,
  fastestRoutePolyline,
  currentLocation,
  startLocation,
  endLocation,
  onMapLoad,
  onPlaceClick,
  onMapClick,
  isSelectingLocation,
  hasPreferences = false,
  travelMode = 'DRIVING',
  className = '',
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const fastestRoutePolylineRef = useRef<google.maps.Polyline | null>(null);
  const currentLocationMarkerRef = useRef<google.maps.Marker | null>(null);
  const outerCircleRef = useRef<google.maps.Marker | null>(null);
  const startMarkerRef = useRef<google.maps.Marker | null>(null);
  const endMarkerRef = useRef<google.maps.Marker | null>(null);
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);

  const { orderedPlaces } = useMemo(() => {
    const ordered = orderPlacesForMap(places);
    return { orderedPlaces: ordered };
  }, [places]);

  // Store callbacks in refs to avoid dependency issues
  const onMapLoadRef = useRef(onMapLoad);
  onMapLoadRef.current = onMapLoad;

  // Initialize the map - only runs once on mount
  const initMap = useCallback(async () => {
    // Skip if already initialized
    if (mapInstanceRef.current) return;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch the API key and user location in parallel for faster load
      const [config, userLocation] = await Promise.all([
        getConfig(),
        getUserLocation(),
      ]);

      if (!config.google_maps_api_key) {
        throw new Error('Google Maps API key not configured');
      }

      // Load Google Maps script
      await loadGoogleMapsScript(config.google_maps_api_key);

      if (!mapRef.current) return;

      // Create map instance centered on user's location (or fallback)
      const map = new window.google.maps.Map(mapRef.current, {
        center: userLocation,
        zoom: 14, // Slightly higher zoom when centered on user location
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER,
        },
      });

      mapInstanceRef.current = map;
      setIsMapReady(true);
      onMapLoadRef.current?.(map);
      setIsLoading(false);
    } catch (err) {
      console.error('Error initializing map:', err);
      setError(err instanceof Error ? err.message : 'Failed to load map');
      setIsLoading(false);
    }
  }, []); // No dependencies - only init once

  // Initialize map on mount
  useEffect(() => {
    initMap();

    return () => {
      // Cleanup markers
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];

      // Cleanup polylines
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      if (fastestRoutePolylineRef.current) {
        fastestRoutePolylineRef.current.setMap(null);
        fastestRoutePolylineRef.current = null;
      }

      // Cleanup current location markers
      if (currentLocationMarkerRef.current) {
        currentLocationMarkerRef.current.setMap(null);
        currentLocationMarkerRef.current = null;
      }
      if (outerCircleRef.current) {
        outerCircleRef.current.setMap(null);
        outerCircleRef.current = null;
      }

      // Cleanup start/end markers
      if (startMarkerRef.current) {
        startMarkerRef.current.setMap(null);
        startMarkerRef.current = null;
      }
      if (endMarkerRef.current) {
        endMarkerRef.current.setMap(null);
        endMarkerRef.current = null;
      }

      // Cleanup map click listener
      if (mapClickListenerRef.current) {
        window.google?.maps?.event?.removeListener(mapClickListenerRef.current);
        mapClickListenerRef.current = null;
      }

      // Reset map instance
      mapInstanceRef.current = null;
      setIsMapReady(false);
    };
  }, [initMap]);

  // Update center when it changes
  useEffect(() => {
    if (mapInstanceRef.current && center && isMapReady) {
      mapInstanceRef.current.setCenter(center);
      // Zoom in when center changes (user selected a location)
      mapInstanceRef.current.setZoom(15);
    }
  }, [center, isMapReady]);

  // Update zoom when it changes
  useEffect(() => {
    if (mapInstanceRef.current && zoom && isMapReady) {
      mapInstanceRef.current.setZoom(zoom);
    }
  }, [zoom, isMapReady]);

  // Update markers when places change
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google || !isMapReady) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Route stops: numbered blue circles. Ranked alternatives: meaningful emoji icons by category.
    let stopNumber = 0;
    orderedPlaces.forEach((place) => {
      const isStopMarker = place.marker_kind !== 'ranked';
      const queryTagKey = getPlaceQueryTagKey(place);
      const rankedEmoji = !isStopMarker ? getRankedPlaceEmoji(queryTagKey) : '';
      const marker = new window.google.maps.Marker({
        position: place.location,
        map: mapInstanceRef.current,
        title: isStopMarker
          ? place.name
          : `${place.name} (${formatPlaceTypeLabel(queryTagKey)})`,
        label: isStopMarker
          ? {
              text: String(++stopNumber),
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
            }
          : undefined,
        icon: isStopMarker
          ? {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 15,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: 'white',
              strokeWeight: 2,
            }
          : makeEmojiMarkerIcon(rankedEmoji, window.google.maps),
      });

      // Add click listener
      if (onPlaceClick) {
        marker.addListener('click', () => onPlaceClick(place));
      }

      const typeLine = !isStopMarker
        ? `<p style="margin: 4px 0 0; font-size: 11px; color: #555;">Preference: ${formatPlaceTypeLabel(queryTagKey)}</p>`
        : '';
      // Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; max-width: 200px;">
            <h3 style="margin: 0 0 4px; font-size: 14px; font-weight: 600;">${place.name}</h3>
            ${place.formatted_address ? `<p style="margin: 0; font-size: 12px; color: #666;">${place.formatted_address}</p>` : ''}
            ${typeLine}
            ${place.rating ? `<p style="margin: 4px 0 0; font-size: 12px;">⭐ ${place.rating}</p>` : ''}
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindow.open(mapInstanceRef.current, marker);
      });

      markersRef.current.push(marker);
    });

    // Fit bounds to show all markers
    if (orderedPlaces.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      orderedPlaces.forEach(place => {
        bounds.extend(place.location);
      });
      mapInstanceRef.current.fitBounds(bounds, 50);
    }
  }, [orderedPlaces, onPlaceClick, isMapReady]);

  // Update fastest route polyline (always shown in black)
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google || !isMapReady) return;

    // Clear existing fastest route polyline
    if (fastestRoutePolylineRef.current) {
      fastestRoutePolylineRef.current.setMap(null);
      fastestRoutePolylineRef.current = null;
    }

    // Draw fastest route polyline (always black); style by travel mode (solid/dashed/dotted)
    if (fastestRoutePolyline) {
      const decodedPath = window.google.maps.geometry?.encoding?.decodePath(fastestRoutePolyline);
      if (decodedPath) {
        const strokeColor = '#000000';
        const icons = getPolylineIconsForTravelMode(travelMode, strokeColor);
        fastestRoutePolylineRef.current = new window.google.maps.Polyline({
          path: decodedPath,
          geodesic: true,
          strokeColor,
          strokeOpacity: icons ? 0 : 0.6,
          strokeWeight: 4,
          icons: icons ?? undefined,
          zIndex: 1,
        });
        fastestRoutePolylineRef.current.setMap(mapInstanceRef.current);
      }
    }
  }, [fastestRoutePolyline, travelMode, isMapReady]);

  // Update selected route polyline (blue when has preferences, black when no preferences)
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google || !isMapReady) return;

    // Clear existing selected route polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    // Draw selected route polyline; style by travel mode (solid/dashed/dotted)
    if (polyline) {
      try {
        const decodedPath = window.google.maps.geometry?.encoding?.decodePath(polyline);
        if (decodedPath && decodedPath.length > 0) {
        const routeColor = hasPreferences ? '#FFC107' : '#000000';
        const icons = getPolylineIconsForTravelMode(travelMode, routeColor);

        polylineRef.current = new window.google.maps.Polyline({
          path: decodedPath,
          geodesic: true,
          strokeColor: routeColor,
          strokeOpacity: icons ? 0 : 0.8,
          strokeWeight: 5,
          icons: icons ?? undefined,
          zIndex: 2,
        });
        polylineRef.current.setMap(mapInstanceRef.current);

        // Fit bounds to show the route
        const bounds = new window.google.maps.LatLngBounds();
        decodedPath.forEach((point: google.maps.LatLng) => bounds.extend(point));
        // Also include fastest route bounds if available
        if (fastestRoutePolylineRef.current) {
          const fastestPath = fastestRoutePolylineRef.current.getPath();
          fastestPath.forEach((point: google.maps.LatLng) => bounds.extend(point));
        }
          mapInstanceRef.current.fitBounds(bounds, 50);
        } else {
          console.warn('Failed to decode polyline or empty path:', polyline);
        }
      } catch (error) {
        console.error('Error decoding/rendering polyline:', error, 'Polyline:', polyline?.substring(0, 50));
      }
    }
    // When polyline is falsy, the ref was already cleared at the start of this effect.
  }, [polyline, hasPreferences, travelMode, fastestRoutePolyline, isMapReady]);

  // Update current location marker
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current || !window.google) return;

    // Clear existing current location markers
    if (currentLocationMarkerRef.current) {
      currentLocationMarkerRef.current.setMap(null);
      currentLocationMarkerRef.current = null;
    }
    if (outerCircleRef.current) {
      (outerCircleRef.current as google.maps.Marker).setMap(null);
      outerCircleRef.current = null;
    }

    // Add current location marker if location is available
    if (currentLocation) {

      // Create outer circle (accuracy indicator)
      outerCircleRef.current = new window.google.maps.Marker({
        position: currentLocation,
        map: mapInstanceRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 20,
          fillColor: '#4285F4',
          fillOpacity: 0.15,
          strokeColor: '#4285F4',
          strokeWeight: 1,
          strokeOpacity: 0.3,
        },
        zIndex: 998,
        clickable: false,
      });

      // Create inner dot (your location)
      currentLocationMarkerRef.current = new window.google.maps.Marker({
        position: currentLocation,
        map: mapInstanceRef.current,
        title: 'Your location',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2,
        },
        zIndex: 999,
        clickable: false,
      });
    }
  }, [currentLocation, isMapReady]);

  // Update start/end location markers
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current || !window.google) return;

    // Update start marker
    if (startLocation) {
      if (startMarkerRef.current) {
        startMarkerRef.current.setPosition(startLocation);
      } else {
        // Green pin icon - custom SVG path
        const pinPath = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';

        startMarkerRef.current = new window.google.maps.Marker({
          position: startLocation,
          map: mapInstanceRef.current,
          title: 'Start location',
          icon: {
            path: pinPath,
            fillColor: '#34C759', // Green color
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 1.5,
            anchor: new window.google.maps.Point(12, 22), // Anchor at the bottom point of the pin
          },
          zIndex: 1000,
        });
      }
    } else {
      if (startMarkerRef.current) {
        startMarkerRef.current.setMap(null);
        startMarkerRef.current = null;
      }
    }

    // Update end marker
    if (endLocation) {
      if (endMarkerRef.current) {
        endMarkerRef.current.setPosition(endLocation);
      } else {
        // Red pin icon - custom SVG path (same as start but red)
        const pinPath = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';

        endMarkerRef.current = new window.google.maps.Marker({
          position: endLocation,
          map: mapInstanceRef.current,
          title: 'End location',
          icon: {
            path: pinPath,
            fillColor: '#FF3B30', // Red color
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 1.5,
            anchor: new window.google.maps.Point(12, 22), // Anchor at the bottom point of the pin
          },
          zIndex: 1000,
        });
      }
    } else {
      if (endMarkerRef.current) {
        endMarkerRef.current.setMap(null);
        endMarkerRef.current = null;
      }
    }
  }, [startLocation, endLocation, isMapReady]);

  // Update map click listener; always listen for map taps.
  useEffect(() => {
    if (!isMapReady || !mapInstanceRef.current || !window.google) return;

    // Remove old listener
    if (mapClickListenerRef.current) {
      window.google.maps.event.removeListener(mapClickListenerRef.current);
      mapClickListenerRef.current = null;
    }

    // Cursor reflects selection mode
    mapInstanceRef.current.setOptions({ draggableCursor: isSelectingLocation ? 'crosshair' : null });

    if (onMapClick) {
      mapClickListenerRef.current = mapInstanceRef.current.addListener(
        'click',
        (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          const location: Location = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
          };
          if (isSelectingLocation) {
            reverseGeocode(location.lat, location.lng)
              .then((result) => onMapClick(location, result.formatted_address ?? undefined))
              .catch(() => onMapClick(location));
            return;
          }
          onMapClick(location);
        }
      );
    }

    return () => {
      if (mapClickListenerRef.current) {
        window.google.maps.event.removeListener(mapClickListenerRef.current);
        mapClickListenerRef.current = null;
      }
    };
  }, [isSelectingLocation, onMapClick, isMapReady]);

  // Retry function for error state
  const handleRetry = useCallback(() => {
    mapInstanceRef.current = null;
    setIsMapReady(false);
    initMap();
  }, [initMap]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="text-center p-4">
          <span className="material-symbols-outlined text-4xl text-red-500 mb-2">error</span>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={handleRetry}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={mapRef} className="w-full h-full" />
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleMap;
